import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Button, Icon, makeStyles, Typography } from '@popmenu/common-ui';
import { Mail } from '@popmenu/web-icons';
import { useSendSignInLinkMutation } from '~/libs/gql/mutations/eat/sendSignInLinkMutation.generated';
import { useReturnTo, withReturnTo } from '../../hooks/useReturnTo';
import { SignInLayout } from '../SignInLayout';

export const RESEND_DELAY_SECONDS = 60;

type CheckEmailLocationState = {
  email?: string
} | undefined

const useStyles = makeStyles(theme => ({
  actions: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    // Lines the text buttons' labels up with the copy above, past their built-in padding
    marginLeft: theme.spacing(-1),
  },
  help: {
    color: theme.palette.text.secondary,
  },
  icon: {
    alignItems: 'center',
    backgroundColor: theme.palette.surface.main,
    borderRadius: '50%',
    display: 'flex',
    fontSize: '1.5rem',
    height: theme.spacing(7),
    justifyContent: 'center',
    width: theme.spacing(7),
  },
}));

const formatCountdown = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export const CheckEmailPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { state } = useLocation<CheckEmailLocationState>();
  const { enqueueSnackbar } = useSnackbar();
  const returnTo = useReturnTo();
  const [secondsLeft, setSecondsLeft] = useState(RESEND_DELAY_SECONDS);
  const [sendSignInLink, { loading }] = useSendSignInLinkMutation();
  const email = state?.email;

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;

    const timeout = setTimeout(() => setSecondsLeft(seconds => seconds - 1), 1000);
    return () => clearTimeout(timeout);
  }, [secondsLeft]);

  // Arriving without an address (e.g. a shared URL) leaves nothing to resend, so start over
  if (!email) return <Redirect to={withReturnTo('/sign-in', returnTo)} />;

  const onResend = async () => {
    try {
      await sendSignInLink({ variables: { email, returnTo } });
      setSecondsLeft(RESEND_DELAY_SECONDS);
      enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Link sent' }), { variant: 'success' });
    } catch {
      enqueueSnackbar(intl.formatMessage({ defaultMessage: "We couldn't send the link. Please try again." }), { variant: 'error' });
    }
  };

  return (
    <SignInLayout
      description={(
        <FormattedMessage
          defaultMessage="We sent a sign-in link to <b>{email}</b>. Tap the link in that email to sign in. It expires in 1 hour."
          values={{
            b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            email,
          }}
        />
      )}
      heading={<FormattedMessage defaultMessage="Check your email" />}
      icon={(
        <Box aria-hidden className={classes.icon}>
          <Icon icon={Mail} />
        </Box>
      )}
      title={intl.formatMessage({ defaultMessage: 'Check your email' })}
    >
      <Box className={classes.actions}>
        <Button disabled={loading || secondsLeft > 0} onClick={() => { void onResend(); }} variant="text">
          {secondsLeft > 0 ? (
            <FormattedMessage
              defaultMessage="Resend link (available in {countdown})"
              values={{ countdown: formatCountdown(secondsLeft) }}
            />
          ) : (
            <FormattedMessage defaultMessage="Resend link" />
          )}
        </Button>
        <Button component={Link} to={withReturnTo('/sign-in', returnTo)} variant="text">
          <FormattedMessage defaultMessage="Use a different email" />
        </Button>
      </Box>
      <Typography className={classes.help} variant="body2">
        <FormattedMessage
          defaultMessage="Can't find it? Check your spam or promotions folder. The link signs you in on whichever device you open it on."
        />
      </Typography>
    </SignInLayout>
  );
};
